import { getProctorAllocation } from "./getProctorAllocation";
import { TareCodegenTst } from "../../proctor/__generated__/SharedProctorSpecifications";
import { applyProctorOverride } from "../../proctor/applyProctorOverride";
import { getProctorOverrides } from "../../proctor/proctorOverrideStore";

/**
 * Proctor test gating the TARE codegen migration: moving TARE reads off the
 * legacy `RNTareBridge` native module onto the codegen'd Turbo modules (e.g.
 * `NativeJSTAccountStatus`). Users in the `Active` bucket use codegen.
 *
 * Re-exported from the generated spec so existing imports keep resolving here.
 */
export { TareCodegenTst };

// The proctor bridge only exposes an async allocation API, so cache the most
// recent result for synchronous callers (e.g. getAccountStatusSync), defaulting
// to the legacy bridge until the async allocation resolves at least once.
let cachedIsEnabled = false;

/**
 * Resolve whether the TARE codegen migration is active for the current viewer,
 * refreshing the synchronous cache as a side effect.
 */
export async function isTareCodegenActive(): Promise<boolean> {
  try {
    const allocation = await getProctorAllocation(TareCodegenTst);
    // Honor locally-forced buckets (debug web groups / prforceGroups) the same
    // way the generated `…ProctorProvider` does for React consumers.
    const baseAllocations = allocation ? { [TareCodegenTst.name]: allocation } : {};
    const merged = applyProctorOverride([TareCodegenTst], baseAllocations, getProctorOverrides());
    const value = merged[TareCodegenTst.name]?.value ?? TareCodegenTst.buckets.Inactive;
    cachedIsEnabled = value >= TareCodegenTst.buckets.Active;
  } catch {
    // Fail closed to the legacy bridge if the lookup rejects; callers never see
    // a rejection.
    cachedIsEnabled = false;
  }
  return cachedIsEnabled;
}

/**
 * Best-effort synchronous read of the codegen migration flag: returns the last
 * cached value (defaulting to `false`/legacy bridge) and kicks off a refresh.
 */
export function isTareCodegenActiveSync(): boolean {
  void isTareCodegenActive();
  return cachedIsEnabled;
}
